import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import palette from 'constants/palette'
import React from 'react'
import { StyleObject, useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { TOUR_ANCHORS } from '../Sandbox/tourAnchors'

/**
 * The Figma "Agent settings" card, shared by the two surfaces that show it: the
 * wizard Testing stage and the Agent Tuning tab (which are meant to mirror each
 * other). ONE card carrying a title, a description and three rows separated by
 * full-bleed hairlines — not three separately bordered cards with gaps of page
 * background between them, which is what shipped.
 *
 * Presentational only: no fetch, no dispatch. Row values arrive as props.
 *
 *   context: the read-only summary (Sandbox/AgentSettingsSummary) and the interactive
 *   host (AgentTuningConfigModals) had drifted into two different treatments of the
 *   same three settings. The chrome lives here so they cannot drift again; each host
 *   still owns its own values and its own interactivity.
 *   Measurements: .context/plans/plan_20260817_rai-wizard-figma-parity.md §1.6.
 */

const CARD_PADDING_PX = 12
const CARD_RADIUS_PX = 8
const ROW_PADDING_Y_PX = 13
// uikit's typography tokens carry no line-height, so the row rhythm the Figma measures
// has to be stated alongside the variant. Label is BodyText6 (10px), value BodyText5
// (12px) — the value is the emphasised half of the pair (bold, grey90) and the label the
// quiet one (uppercase, grey70). That INVERTS the shipped hierarchy, which had a bold
// 16px label over a grey 14px value.
const ROW_LABEL_LINE_HEIGHT_PX = 14
const ROW_LABEL_LETTER_SPACING = '0.06em'
const ROW_VALUE_LINE_HEIGHT_PX = 18
const DESCRIPTION_LINE_HEIGHT_PX = 16
const CHEVRON_SIZE_PX = 11

// Exported so the geometry is unit-testable: styletron's css() is a no-op in the
// jsdom test env, so these rules can never be asserted through the rendered DOM.
export const buildSettingsCardStyle = (): StyleObject => ({
  backgroundColor: 'white',
  border: `1px solid ${merchantTheme.colors.grey20}`,
  borderRadius: `${CARD_RADIUS_PX}px`,
  // The hairlines are the row separators, so they must reach both card edges. Rows
  // therefore carry their own horizontal padding and the card clips them at its radius.
  overflow: 'hidden',
})

/**
 * The full-bleed 1px hairline that separates rows, INSTEAD OF a gap of page background.
 * `borderTop` on every row (rather than borderBottom) is what leaves the last row's
 * edge clean against the card.
 *
 * It lives on a plain wrapper and must NEVER move onto the row's Button. baseui emits
 * `borderTopWidth` / `borderTopStyle` / `borderTopColor` LONGHANDS for BaseButton, and a
 * consumer className carrying the `borderTop` shorthand is a separate atomic class, so
 * which one paints comes down to styletron's injection order — baseui's wins. That is
 * exactly how these dividers came out invisible on the real page while every other rule
 * on the same className (padding, background, layout) applied normally.
 */
export const buildSettingsDividerStyle = (): StyleObject => ({
  borderTop: `1px solid ${merchantTheme.colors.grey10}`,
})

// Everything both row forms share EXCEPT padding, which the two have to express
// differently — see buildSettingsRowOverrideStyle for why the interactive form cannot
// use the shorthand.
const rowLayout = (interactive: boolean): StyleObject => ({
  width: '100%',
  display: 'flex',
  // Cross axis, so it flips meaning with the direction: 'center' centres the row's
  // label-stack against its chevron VERTICALLY, but on the read-only column it would
  // centre the label and value HORIZONTALLY — which is not what either wants.
  alignItems: interactive ? 'center' : 'flex-start',
  justifyContent: interactive ? 'space-between' : 'flex-start',
  flexDirection: interactive ? 'row' : 'column',
  backgroundColor: 'white',
  textAlign: 'left',
})

export const buildSettingsRowStyle = (interactive: boolean): StyleObject => ({
  ...rowLayout(interactive),
  // Safe as a shorthand here: this form is applied via className to a plain element
  // that has no competing padding longhands.
  padding: `${ROW_PADDING_Y_PX}px ${CARD_PADDING_PX}px`,
})

/**
 * The interactive row's layout, for `overrides.BaseButton.style` — NOT for `className`.
 *
 * Same lesson as the divider, one property set over: baseui styles BaseButton as an
 * `inline-flex` with `justify-content: center`, and a consumer className is a separate
 * atomic class competing with it, so styletron's injection order decides the winner.
 * It lost — the row rendered content-width and centre-justified, so the label/value
 * stack sat mid-row with the chevron jammed against it instead of spanning the card.
 * An `overrides` block is applied LAST and therefore wins deterministically, which is
 * why every other styled Button in this feature (ChatInput's send, ReasoningChecklist's
 * Tweak link, SimulatorEngine's text links) uses one.
 *
 *   context: those three are precedent for the overrides-not-className mechanism only —
 *   none of them restates a focus ring, so do not read them as a template for the
 *   `:focus-visible` block below.
 *
 *   context: "applied last" is per-PROPERTY, not wholesale — styletron deep-MERGES this
 *   block into baseui's base style. Same-key declarations therefore win deterministically,
 *   which is the whole point; but a SHORTHAND never collides with baseui's longhands, so it
 *   emits a second atomic class at equal specificity and injection order decides again.
 *   That is why padding is stated as four longhands below, matching the
 *   paddingTop/Right/Bottom/Left baseui sets, and why `border: 'none'` is absent: the base
 *   style already zeroes all four border widths and sets all four styles to `none`, so a
 *   shorthand reset would buy nothing except that same race. The focus ring still has to be
 *   restated — baseui's base sets `outline: 'none'` and drives its own ring off an
 *   `$isFocusVisible` prop, not the CSS pseudo-class (WCAG 2.4.7).
 *
 *   context: the `disabled` parameter is not cosmetic. `cursor` and `:hover` are
 *   same-key declarations that deep-merge OVER baseui's own disabled treatment, so
 *   leaving them at their enabled values would paint a view-only operator's inert row
 *   as pointer-and-highlight — an affordance that no longer exists (AI-64).
 */
export const buildSettingsRowOverrideStyle = (
  disabled = false
): StyleObject => ({
  ...rowLayout(true),
  paddingTop: `${ROW_PADDING_Y_PX}px`,
  paddingBottom: `${ROW_PADDING_Y_PX}px`,
  paddingLeft: `${CARD_PADDING_PX}px`,
  paddingRight: `${CARD_PADDING_PX}px`,
  cursor: disabled ? 'not-allowed' : 'pointer',
  // Omitted entirely rather than set to an empty block when disabled, so no stray
  // atomic class is emitted for a state that must not react to the pointer.
  ...(disabled
    ? {}
    : { ':hover': { backgroundColor: merchantTheme.colors.grey05 } }),
  // Restated because replacing the preset drops baseui's own focus treatment, and a
  // keyboard user has to be able to see which row they are on (WCAG 2.4.7). Inset so
  // the ring is not clipped by the card's overflow:hidden.
  ':focus-visible': {
    outline: `2px solid ${merchantTheme.colors.belizeHole50}`,
    outlineOffset: '-2px',
  },
})

// Applied ON TOP OF Text.BodyText6 / Text.BodyText5, which supply size, family and
// tracking; these carry only what the tokens don't.
const labelStyle: StyleObject = {
  lineHeight: `${ROW_LABEL_LINE_HEIGHT_PX}px`,
  // CSS-only, so a screen reader still reads the real sentence-case label.
  textTransform: 'uppercase',
  letterSpacing: ROW_LABEL_LETTER_SPACING,
  margin: 0,
}

const valueStyle: StyleObject = {
  lineHeight: `${ROW_VALUE_LINE_HEIGHT_PX}px`,
  margin: 0,
}

type CardProps = {
  title: string
  // Omitted on surfaces that don't want the explanatory line under the title.
  description?: string
  // The rows. `children` rather than a `rows` prop so neither host has to hand a JSX
  // tree through a prop, and so each keeps ownership of its own row semantics.
  children: React.ReactNode
}

export const AgentSettingsCard = ({
  title,
  description,
  children,
}: CardProps) => {
  const [css] = useStyletron()
  return (
    // The wizard tour spotlights this card. The anchor lives here rather than on the
    // read-only summary because the summary is the branch NEITHER surface renders —
    // both pass an interactive settingsCard — so on master the tour's agentSettings
    // step had no target at all.
    <div
      data-tour={TOUR_ANCHORS.agentSettings}
      className={css(buildSettingsCardStyle())}
    >
      <div className={css({ padding: `${CARD_PADDING_PX}px` })}>
        <Text.BodyText3 bold tag="div">
          {title}
        </Text.BodyText3>
        {description && (
          <Text.BodyText5
            tag="div"
            color="grey70"
            className={css({
              lineHeight: `${DESCRIPTION_LINE_HEIGHT_PX}px`,
              marginTop: '2px',
            })}
          >
            {description}
          </Text.BodyText5>
        )}
      </div>
      {children}
    </div>
  )
}

type RowProps = {
  label: string
  // Absent for a row with no single summarisable value (escalation rules carries two
  // thresholds) or while the config GET is still in flight.
  value?: string
  /**
   * Supplying a handler makes the row a modal-opening button. Omitting it renders a
   * static `dt`/`dd` pair for the read-only summary — the caller must wrap read-only
   * rows in a `<dl>` so the label/value relationship survives (WCAG 1.3.1).
   *
   * The interactive shape can't use dt/dd: the accessible name has to be
   * "<label> <value>", which means one button wrapping both, and a `<dl>` of buttons
   * would convey nothing. The button's own name carries the pairing instead.
   */
  onClick?: () => void
  /**
   * Renders the interactive row inert — a view-only operator (no CASL
   * manage/RecoveryAI) may READ the current value but must not be able to open the
   * editor behind it (AI-64).
   *
   *   context: a real `disabled` attribute, not `aria-disabled`. The row is a uikit
   *   Button, which renders a native `<button>`; `aria-disabled` is the anchor-only
   *   workaround for elements that have no `disabled` of their own. Ignored on the
   *   read-only branch, which renders no control to disable.
   */
  disabled?: boolean
}

export const AgentSettingsRow = ({
  label,
  value,
  onClick,
  disabled = false,
}: RowProps) => {
  const [css] = useStyletron()
  const dividerClass = css(buildSettingsDividerStyle())

  if (!onClick) {
    // Read-only: the divider and the row layout share one element, which is also the
    // `<dl>`'s permitted div child wrapping this pair.
    return (
      <div className={`${dividerClass} ${css(buildSettingsRowStyle(false))}`}>
        <Text.BodyText6 tag="dt" color="grey70" className={css(labelStyle)}>
          {label}
        </Text.BodyText6>
        {value && (
          <Text.BodyText5 bold tag="dd" className={css(valueStyle)}>
            {value}
          </Text.BodyText5>
        )}
      </div>
    )
  }

  return (
    // Wrapper carries the hairline; the Button carries only layout. See
    // buildSettingsDividerStyle for why the two cannot share an element.
    <div className={dividerClass}>
      <Button
        kind="minimal"
        // Signals to AT that activating the row opens a modal dialog (a focus context
        // shift), not just toggles content (WAI-ARIA button pattern).
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={onClick}
        // overrides, not className — see buildSettingsRowOverrideStyle.
        overrides={{
          BaseButton: { style: buildSettingsRowOverrideStyle(disabled) },
        }}
      >
        <span className={css({ display: 'flex', flexDirection: 'column' })}>
          <Text.BodyText6 color="grey70" className={css(labelStyle)}>
            {label}
          </Text.BodyText6>
          {value && (
            <Text.BodyText5 bold className={css(valueStyle)}>
              {value}
            </Text.BodyText5>
          )}
        </span>
        {/* Decorative: the row's own accessible name already says what it opens. */}
        <i
          className={`fa fa-chevron-right ${css({
            color: palette.grey70,
            fontSize: `${CHEVRON_SIZE_PX}px`,
            flex: '0 0 auto',
          })}`}
          aria-hidden="true"
        />
      </Button>
    </div>
  )
}
